import type { JDDetail, JDSkill, JDStatusName, JDSummary } from "@/api/jd-queries";
import type { JDQuestion, JDQuestions } from "@/api/jd-question-queries";

/**
 * What the job-description screens say about a JD — DESIGN §20.11 as redesigned on 2026-10-15. Each
 * step's one-line state, the publish checklist, a question's place in its life and a list row's next
 * step are worked out here, from what the API already returns, so the list and the page cannot
 * describe the same JD two ways. No React here: react-refresh wants component files to export
 * components only.
 */

export type StepKey = "details" | "skills" | "questions" | "mix" | "publish" | "activity";
export const STEP_KEYS: readonly StepKey[] = ["details", "skills", "questions", "mix", "publish", "activity"];
export const STEP_TITLE: Record<StepKey, string> = {
  details: "Details",
  skills: "Skills",
  questions: "Questions",
  mix: "Interview mix",
  publish: "Preview & publish",
  activity: "Activity",
};

/** `ok` nothing to do, `warn` somebody's job, `plain` a setting with no right answer. */
export type Tone = "ok" | "warn" | "plain";

// The bucket tokens stay on screen beside these (BRANDING §6: data in mono), so grepping one still lands.
const BUCKET_LABEL: Record<string, string> = {
  technical: "Technical",
  project_domain: "Project & domain",
  problem_solving: "Problem solving",
  soft_skill: "Soft skills",
  introduction: "Introduction",
};
export const bucketLabel = (bucket: string) => BUCKET_LABEL[bucket] ?? bucket;

// The order an interview takes them in, so every list of buckets leads with the part that is always asked.
const BUCKET_ORDER = ["introduction", "technical", "project_domain", "problem_solving", "soft_skill"];
const rank = (bucket: string) => {
  const at = BUCKET_ORDER.indexOf(bucket);
  return at < 0 ? BUCKET_ORDER.length : at;
};
export function byBucket<T>(items: T[], key: (item: T) => string): T[] {
  return [...items].sort((a, b) => rank(key(a)) - rank(key(b)) || key(a).localeCompare(key(b)));
}

/** The usual mix, shown until a JD fixes its own (§20.17a); ipp-platform sends these unless told otherwise. */
export const DEFAULT_COUNTS: Record<string, number> = {
  technical: 8,
  project_domain: 2,
  problem_solving: 2,
  soft_skill: 2,
};

/** How many of each bucket a pick for the JD asks for: its own numbers if it has them, else the usual mix. */
export function countsFor(buckets: string[], counts: Record<string, number> | null): Record<string, number> {
  return Object.fromEntries(buckets.map((b) => [b, counts ? (counts[b] ?? 0) : (DEFAULT_COUNTS[b] ?? 0)]));
}

/**
 * §20.9's JD share of one bucket: the policy's percent, rounded down but never to none while the JD has
 * a question there, and no more than it has — what it cannot fill, the related share does.
 */
export function jdShare(want: number, percent: number, available: number): number {
  let share = Math.floor((want * percent) / 100);
  if (percent > 0 && available > 0 && share === 0 && want > 0) share = 1;
  return Math.min(share, available);
}

/** What a status means for the members who might practise the JD, beside the literal chip. */
export const STATUS_MEANING: Record<JDStatusName, string> = {
  in_review: "Not visible to members yet",
  active: "Live on ipp-platform",
  archived: "Off the start form",
  discarded: "Discarded — not in use",
};

export function experience(lo: number | null, hi: number | null): string {
  if (lo === null && hi === null) return "experience not stated";
  if (hi === null) return `${lo}+ years`;
  if (lo === null) return `up to ${hi} years`;
  return `${lo}–${hi} years`;
}

export function audienceOf(jd: Pick<JDSummary, "audience_members" | "audience_teams">): string {
  const members = (jd.audience_members ?? []).length;
  const teams = (jd.audience_teams ?? []).length;
  if (members === 0 && teams === 0) return "every member";
  const parts = [];
  if (members) parts.push(`${members} member${members === 1 ? "" : "s"}`);
  if (teams) parts.push(`${teams} team${teams === 1 ? "" : "s"}`);
  return parts.join(" and ");
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------- skills

/** Technical and domain skills point at a stack; soft and other ones are covered by their buckets. */
export const isMappable = (sk: Pick<JDSkill, "category">) =>
  sk.category === "technical" || sk.category === "domain";

export type SkillGroup = "attention" | "mapped" | "unmappable" | "ignored";

/**
 * Where a skill sits on the Skills step. Soft and other skills are stored `ignored` by the server
 * (core/jd.py:map_skill), so category decides before status does, or every soft skill would read as
 * one somebody chose to ignore.
 */
export function skillGroup(sk: JDSkill): SkillGroup {
  if (!isMappable(sk)) return "unmappable";
  if (sk.status === "ignored") return "ignored";
  return sk.questions === 0 ? "attention" : "mapped";
}

// ---------------------------------------------------------------- steps

export interface StepState {
  tone: Tone;
  line: string;
}

export function stepStates({
  jd,
  questions,
  drafts,
  skills,
  counts,
  policyName,
}: {
  jd: JDDetail;
  questions: JDQuestions | undefined;
  drafts: number;
  skills: JDSkill[];
  counts: Record<string, number> | null;
  policyName: string;
}): Record<StepKey, StepState> {
  const attention = skills.filter((sk) => skillGroup(sk) === "attention").length;
  const mapped = skills.filter((sk) => skillGroup(sk) === "mapped").length;
  const waiting = questions?.counts.own_waiting ?? 0;
  const own = questions?.counts.own ?? jd.jd.own_questions;
  const matched = questions?.counts.matched ?? jd.jd.matched_questions;
  const { blockers, warnings } = jd.readiness;
  const status = jd.jd.status;

  const questionWork = [drafts && plural(drafts, "draft"), waiting && `${waiting} waiting`].filter(Boolean);
  const total = counts ? Object.values(counts).reduce((n, c) => n + c, 0) : 0;
  const shown = counts ? `${total} question${total === 1 ? "" : "s"}` : "ipp-platform decides";

  let publish: StepState;
  if (status === "active")
    publish = { tone: "ok", line: warnings.length ? `Live · ${plural(warnings.length, "warning")}` : "Live" };
  else if (status === "archived") publish = { tone: "plain", line: "Archived" };
  else if (status === "discarded") publish = { tone: "plain", line: "Discarded" };
  else if (blockers.length) publish = { tone: "warn", line: "Can't publish yet" };
  else
    publish = {
      tone: "ok",
      line: warnings.length ? `Ready · ${plural(warnings.length, "warning")}` : "Ready",
    };

  const noExperience = jd.jd.experience_min === null && jd.jd.experience_max === null;
  return {
    details: noExperience
      ? { tone: "warn", line: "Experience not stated" }
      : {
          tone: "ok",
          line: `${experience(jd.jd.experience_min, jd.jd.experience_max)} · ${audienceOf(jd.jd)}`,
        },
    skills: attention
      ? { tone: "warn", line: `${attention} need${attention === 1 ? "s" : ""} attention` }
      : { tone: "ok", line: `${mapped} mapped` },
    questions: questionWork.length
      ? { tone: "warn", line: questionWork.join(" · ") }
      : { tone: "ok", line: `${own} own · ${matched} matched` },
    mix: { tone: "plain", line: `${policyName} · ${shown}` },
    publish,
    activity: { tone: "plain", line: plural(jd.events.length, "event") },
  };
}

/** Why Publish cannot be pressed right now, written out beside it rather than hidden in a tooltip. */
export function publishBlock(jd: JDDetail, dirty: boolean): string | null {
  if (dirty) return "Save your changes first";
  if (!jd.readiness.can_publish) return "Something blocks it — see Preview & publish";
  return null;
}

// ---------------------------------------------------------------- publish checklist

export interface CheckItem {
  tone: "ok" | "warn" | "block";
  title: string;
  detail?: string | undefined;
  fix?: StepKey | undefined;
}

/**
 * The step that fixes a readiness message. The server words them (core/jd.py:readiness) and there are
 * three kinds of warning; only "no questions of its own" is fixed on Questions, the rest on Skills.
 */
function fixFor(message: string): StepKey {
  return message.startsWith("No questions of its own") ? "questions" : "skills";
}

export function checklist(jd: JDDetail, questions: JDQuestions | undefined): CheckItem[] {
  const { blockers, warnings } = jd.readiness;
  const items: CheckItem[] = blockers.map((b) => ({ tone: "block", title: b, fix: fixFor(b) }));
  if (blockers.length === 0) {
    const stacks = [
      ...new Set(
        jd.skills
          .filter((sk) => sk.category === "technical" && sk.stack_path && sk.questions > 0)
          .map((sk) => sk.stack_path as string),
      ),
    ];
    items.push({
      tone: "ok",
      title: "The technical round can be filled",
      detail: stacks.length
        ? `${stacks.join(", ")} ${stacks.length === 1 ? "has" : "have"} approved questions.`
        : undefined,
    });
  }
  for (const w of warnings) items.push({ tone: "warn", title: w, fix: fixFor(w) });
  if (!warnings.some((w) => fixFor(w) === "questions")) {
    const own = questions?.counts;
    items.push({
      tone: "ok",
      title: "It has questions of its own",
      detail: own ? `${own.own_served} live, ${own.own_waiting} on their way.` : undefined,
    });
  }
  return items;
}

// ---------------------------------------------------------------- questions

export interface Place {
  status: string;
  words: string;
  tone: "good" | "attention" | "pipe" | "muted";
}

const ON_ITS_WAY = new Set(["raw", "extracted", "split_checked", "enriched", "embedded", "dedup_checked"]);

/** Where one of the JD's own questions stands, in words a Resource Manager can act on. */
export function placeOf(q: JDQuestion): Place {
  if (q.is_excluded)
    return {
      status: q.canonical_status ?? q.status,
      words: "Excluded — never drawn for this JD",
      tone: "muted",
    };
  if (q.canonical_id !== null) {
    return {
      status: q.canonical_status ?? q.status,
      words: `Already in the bank — linked to #${q.canonical_id} instead of added twice`,
      tone: "muted",
    };
  }
  if (q.status === "approved") {
    return q.served
      ? { status: q.status, words: "Live — drawn for this JD", tone: "good" }
      : { status: q.status, words: "Approved", tone: "good" };
  }
  if (q.status === "pending_review" || q.status === "stale") {
    return { status: q.status, words: "Waiting for a reviewer", tone: "attention" };
  }
  if (ON_ITS_WAY.has(q.status))
    return { status: q.status, words: "On its way — being prepared for review", tone: "pipe" };
  if (q.status === "rejected") return { status: q.status, words: "Rejected by a reviewer", tone: "muted" };
  if (q.status === "duplicate")
    return { status: q.status, words: "Folded into another question", tone: "muted" };
  return { status: q.status, words: q.status, tone: "muted" };
}

// ---------------------------------------------------------------- the list

export interface NextStep {
  tone: Tone | "block";
  text: string;
  detail: string;
}

/** A list row's "what does this JD need", from the summary the list already has. */
export function nextStep(jd: JDSummary): NextStep {
  const gaps = jd.gaps ? plural(jd.gaps, "gap") : "";
  if (jd.status === "discarded") return { tone: "plain", text: "Discarded", detail: jd.discard_reason ?? "" };
  if (jd.status === "archived")
    return { tone: "plain", text: "Archived", detail: "Publish again from its page" };
  if (jd.status === "active") return { tone: "ok", text: "Live", detail: gaps || "On the start form" };
  // Readiness needs the per-stack question counts only the detail carries, so the list claims "can't"
  // for the one case it can see — no mapped stack at all — and never claims "ready".
  if (jd.stacks.length === 0)
    return { tone: "block", text: "Can't publish yet", detail: "Map a technical skill to a stack" };
  return { tone: "plain", text: "Review & publish", detail: gaps || "No gaps" };
}
